import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { resolvePlayerMark, useBlipFeed } from '../hooks/useBlipFeed.js';
import AudioSettings from './AudioSettings.jsx';
import { useFreeze } from './freezeOwner.js';
import MissionList from './MissionList.jsx';
import {
  BLIP_COLORS,
  BLIP_LEGEND,
  MAP_COLORS,
  MARKER_COLORS,
  ZONE_LABELS,
  blitBase,
  districtLabels,
  drawBlip,
  drawMarkerLabel,
  drawMissionMarker,
  drawPlaceholder,
  drawPlayerMarker,
  drawVillages,
  fitScale,
  getBase,
  viewX,
  viewY,
} from './mapRender.js';

/**
 * @file Esc menu + the full-screen MAP view.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW THE PAUSE ACTUALLY WORKS
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine.loop.setTimeScale(0)`, not `loop.stop()`. With the scale at zero the
 * frame delta is zero, so no fixed step runs — the sim, the day/night clock and
 * every system are frozen — but `onRender` still fires, which means the world
 * keeps being drawn behind the overlay and a window resize still reaches the
 * renderer. `stop()` would give you a stale framebuffer and a canvas that does
 * not resize while the menu is open.
 *
 * The effect's CLEANUP always restores 1. That is the guarantee that matters:
 * unmounting the HUD (route change, engine teardown) can never leave a live
 * engine frozen at zero.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * KEYBOARD: WHY A CAPTURE LISTENER ON `window`
 * ─────────────────────────────────────────────────────────────────────────────
 * `engine/input/Keyboard.js` listens for `keydown` on `window` in the BUBBLE
 * phase. A capture-phase listener on the same node runs first, so
 * `stopImmediatePropagation()` is what actually keeps a key out of the sim —
 * plain `stopPropagation()` would not, because both listeners sit on the same
 * target.
 *
 * Three rules fell out of that, each of them a bug if you drop it:
 *  1. Only `keydown` is swallowed, never `keyup`. Swallowing keyup would strand
 *     a key that was HELD when the menu opened in `Input.downKeys` forever, and
 *     the player would walk off on resume.
 *  2. Events whose target is inside the overlay are never touched, or Tab +
 *     Enter/Space on the RESUME button would die before React saw them.
 *  3. Entering and leaving the menu calls `Input.clear()` on the gameplay
 *     system (reached through `engine.systems`, no engine edit needed), which
 *     is the same thing the window-blur handler does and for the same reason.
 *
 * Esc toggles: playing → PAUSED → playing, and MAP/MISSIONS → PAUSED.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ M IS THE MAP, AND IT IS A DIFFERENT LADDER FROM Esc ON PURPOSE
 * ─────────────────────────────────────────────────────────────────────────────
 * Esc is *one step back*. M is *the map, or out of it* — from playing, from the
 * pause screen and from the missions list it goes straight to MAP, and from MAP
 * it goes straight back to PLAYING rather than to PAUSED. That asymmetry is the
 * point: the player who presses M is checking where they are going, and landing
 * them in a menu they never opened on the way out would make the key cost more
 * than it saves. Esc still walks the ladder, so MAP → PAUSED is one press away
 * for anyone who wanted the menu.
 *
 * ⚠ IT LIVES HERE, NOT IN `Gameplay`'s `input.onKey`, AND IT ADDS NO LISTENER.
 * Every other gameplay key (F, R, X, P) is bound on the sim's `Input` because it
 * ends in a MODEL method; the map is not a model, it is this component's own
 * `view` state, and routing M through the bus would mean a channel whose only
 * subscriber is a `useState` setter three files away. It is handled inside the
 * capture listener this component ALREADY registers at mount, so the registration
 * order that `docs/ui.md` documents (and that this project has paid for once) is
 * untouched: no new listener, no new position in the queue.
 *
 * ⚠ THE MODIFIER GUARD IS MANDATORY, exactly as it is on `Gameplay`'s R/X/P.
 * Cmd+M MINIMISES THE WINDOW on macOS and Ctrl+M is bound in several browsers;
 * swallowing either to open a map nobody asked for is the same trade those
 * guards refuse. Nothing is `preventDefault`ed here — a bare M has no browser
 * default worth cancelling — but it IS `stopImmediatePropagation`ed, or the sim's
 * bubble-phase `Keyboard` would bank a key the player used on the HUD.
 *
 * ⚠ A MODAL BEATS IT FOR FREE. `ShopPanel`, `PhonePanel`, `PhoneCall` and
 * `Cutscene` each swallow EVERY key while open (their fall-through calls
 * `stopImmediatePropagation` unconditionally) and each registers in a
 * `useLayoutEffect` at mount, ahead of this component's passive one — so M can
 * never open a map over a till, a call or a briefing, and no test here has to
 * assert that it does not.
 *
 * ⚠ ONE other thing binds Esc now — `ui/hud/Cutscene.jsx`, which SWALLOWS it
 * (capture phase, `stopImmediatePropagation`) so a briefing cannot be paused
 * over. That is deliberate and it is what keeps the time-scale bookkeeping
 * single-owner: two overlays that both freeze the loop would have the second
 * one's cleanup restore it to 1 while the first is still up.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Keys the sim must not see while the menu is open. NOT Shift — see below. */
const GAME_KEYS = new Set([
  'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyQ', 'KeyE',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'BracketLeft', 'BracketRight',
]);

export default function PauseMenu({ engine }) {
  /** @type {['none'|'pause'|'map'|'missions'|'settings', Function]} */
  const [view, setView] = useState('none');
  /** The quit confirmation. Local by design — see the button block below. */
  const [quitting, setQuitting] = useState(false);
  const viewRef = useRef(view);
  const feed = useBlipFeed(engine);
  const navigate = useNavigate();

  const resume = useCallback(() => setView('none'), []);

  // Leaving the pause screen — by Esc, by Resume, or into the map — must not
  // leave the confirmation armed behind it, or the next pause opens already
  // asking a question nobody just asked for.
  useEffect(() => {
    if (view !== 'pause') setQuitting(false);
  }, [view]);

  // ── the pause itself ───────────────────────────────────────────────────────
  // ⚠ THE FREEZE IS A CLAIM NOW, NOT A WRITE. This used to call
  // `setTimeScale(view === 'none' ? 1 : 0)` on every view change and
  // `setTimeScale(1)` from its cleanup — correct while the pause menu was the
  // only thing that froze the world, and the `docs/shops.md` "last one to
  // restore wins" defect once it was not. `freezeOwner.js` owns the transitions;
  // this component only says whether IT wants the world stopped.
  // ⚠ The boolean matters: `view` has four values but only two freeze states, so
  // moving PAUSED → MAP must not release and re-acquire.
  const paused = view !== 'none';
  useFreeze(engine, 'pause-menu', paused);
  useEffect(() => {
    viewRef.current = view;
    // A key held across the transition must not survive it — same reasoning as
    // `Input`'s own blur handler.
    clearGameInput(engine);
  }, [engine, view]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        // Any sub-view goes back to PAUSED; PAUSED resumes. One key, one
        // predictable ladder.
        setView((v) => (v === 'none' ? 'pause' : v === 'pause' ? 'none' : 'pause'));
        return;
      }
      // M — THE MAP. Its own ladder (see the header): anything → MAP, MAP →
      // playing. Handled beside Esc and before the `data-hud-overlay` check, so
      // it still closes the map while the Back button holds focus; there is no
      // text input anywhere in this HUD for that to cost anything.
      if (e.code === 'KeyM') {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        e.stopImmediatePropagation();
        setView((v) => (v === 'map' ? 'none' : 'map'));
        return;
      }
      if (viewRef.current === 'none') return;
      // Rule 2: the overlay's own controls keep their keyboard.
      const t = /** @type {any} */ (e.target);
      if (t?.closest?.('[data-hud-overlay]')) return;
      // Rule 1 + the capture-phase note: swallow game keys, keydown only, and
      // do NOT preventDefault — the page cannot scroll (body is overflow
      // hidden) and cancelling would only cost us browser affordances.
      if (GAME_KEYS.has(e.code)) e.stopImmediatePropagation();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  if (view === 'none') {
    return (
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setView('pause')}
        aria-label="Pause"
        title="Pause (Esc)"
        className="pointer-events-auto absolute left-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-md border border-hairline bg-void/80 text-hud-dim backdrop-blur-sm transition hover:border-gold hover:text-gold"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <rect x="5" y="4" width="4.5" height="16" rx="1" fill="currentColor" />
          <rect x="14.5" y="4" width="4.5" height="16" rx="1" fill="currentColor" />
        </svg>
      </button>
    );
  }

  if (view === 'map') {
    return <FullMap engine={engine} feed={feed} onBack={() => setView('pause')} />;
  }

  if (view === 'missions') {
    return <MissionList engine={engine} onBack={() => setView('pause')} />;
  }

  // ⚠ A THIRD SUB-VIEW COSTS NOTHING IN THE KEYBOARD CONTRACT, and that is by
  // construction rather than by luck: Esc's ladder is `v === 'pause' ? 'none' :
  // 'pause'`, so every sub-view present and future goes back one step without
  // this branch being named anywhere in the handler, and the effect above
  // already calls `clearGameInput` on EVERY view change. The panel carries
  // `data-hud-overlay` (rule 2) so its slider keeps its arrow keys.
  // ⚠ The Escape effect's `[]` deps stay `[]` — `docs/ui.md` and `docs/shops.md`
  // both record what growing that array costs, and nothing added here needs it.
  if (view === 'settings') {
    return <AudioSettings engine={engine} onBack={() => setView('pause')} />;
  }

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-40 flex flex-col items-center justify-center bg-void/85 backdrop-blur-[2px]"
    >
      <h1 className="gta-title font-display text-5xl leading-none sm:text-6xl">PAUSED</h1>

      <div className="mt-12 flex w-60 flex-col gap-2">
        <MenuButton onClick={resume}>Resume</MenuButton>
        <MenuButton onClick={() => setView('missions')}>Missions</MenuButton>
        {/* ⚠ THE KEY IS PRINTED ON THE BUTTON, which is the `AmmoBar` rule: a
            binding and the pixel that documents it must not be able to drift
            apart, and a player who never opens this menu has no other place to
            learn that M exists at all. */}
        <MenuButton onClick={() => setView('map')} hint="M">Map</MenuButton>
        {/* No longer `disabled title="Not built yet"`. The screen behind it is
            AUDIO ONLY, and it says so in its own subtitle rather than in this
            label: the button is the door to settings, and a door renamed after
            the one room currently behind it has to be renamed again the day a
            second room opens. */}
        <MenuButton onClick={() => setView('settings')}>Settings</MenuButton>

        {/* ⚠ QUIT IS LAST, SEPARATED, AND IT ASKS. It replaces the top-right
            `Menu` link that used to sit over live gameplay one mis-tap from the
            wanted meter — going to `/` disposes the level and takes the wallet,
            the pouch, the mission book and the wanted level with it, so it is
            the most destructive control in the game and it had no confirmation
            at all. Here the player has already stopped to open a menu, and the
            second press is the confirmation. `quitting` is LOCAL state: this is
            the one control whose whole purpose is to leave, so there is nothing
            for the engine to own. */}
        <div className="mt-6 border-t border-hairline pt-4">
          {quitting ? (
            <div className="flex flex-col gap-2">
              <p className="px-1 pb-1 font-hud text-[11.5px] leading-snug text-hud-dim">
                Quit to the main menu? This session — your money, ammo and
                mission progress — is lost.
              </p>
              <MenuButton onClick={() => navigate('/')} danger>
                Quit — lose progress
              </MenuButton>
              <MenuButton onClick={() => setQuitting(false)}>Cancel</MenuButton>
            </div>
          ) : (
            <MenuButton onClick={() => setQuitting(true)}>Quit to main menu</MenuButton>
          )}
        </div>
      </div>

      <p className="mt-10 font-hud text-[11px] uppercase tracking-[0.32em] text-hud-faint">
        Esc to resume
      </p>
    </div>
  );
}

/**
 * Slab button, GTA-menu flavoured: dark bar, gold rule that slides in on hover.
 * `onMouseDown` cancels focus so the canvas keeps the keyboard — the same rule
 * every HUD control in this project follows.
 *
 * ⚠ `danger` is the ONE destructive variant (the quit confirmation). It only
 * changes the hover colour and the gold rule to `siren` — the shape stays
 * identical, because a button that is a different SIZE from its neighbour is a
 * mis-tap generator, and this is the one control in the menu you must not hit
 * by accident.
 *
 * `hint` prints the KEY that does the same thing, right-aligned and dim. It is
 * the only optional decoration here and it stays out of the flow (`absolute`),
 * so a button that has one is exactly the same shape as a button that does not —
 * which is the whole reason `danger` only changes colours.
 *
 * @param {{onClick?: Function, disabled?: boolean, danger?: boolean, title?: string,
 *   hint?: string, children: any}} props
 */
function MenuButton({ onClick, disabled = false, danger = false, title, hint, children }) {
  const rule = danger ? 'bg-siren' : 'bg-gold';
  const live = danger
    ? 'border-hairline bg-ink/80 text-hud hover:border-siren hover:bg-steel hover:text-siren'
    : 'border-hairline bg-ink/80 text-hud hover:border-gold hover:bg-steel hover:text-gold';
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={disabled ? undefined : onClick}
      style={{ touchAction: 'manipulation' }}
      className={`group relative overflow-hidden rounded-[3px] border px-5 py-2.5 text-left font-display text-lg uppercase tracking-wide transition ${
        disabled ? 'cursor-not-allowed border-hairline/60 bg-ink/40 text-hud-faint/60' : live
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute inset-y-0 left-0 w-1 ${rule} transition-transform ${
          disabled ? 'scale-y-0' : 'origin-center scale-y-0 group-hover:scale-y-100'
        }`}
      />
      {children}
      {hint ? (
        <span
          aria-hidden="true"
          className="absolute right-4 top-1/2 -translate-y-1/2 font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint"
        >
          {hint}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The whole island at a fitted scale. Same base bitmap as the minimap (see
 * `mapRender.js`) — the only differences are the scale, the district labels and
 * the legend. VIEW ONLY: no click handling, no waypoints.
 *
 * Redraw is on mount and on resize, and that is enough: the loop is frozen, so
 * nothing on this map can move while it is open.
 *
 * @param {{engine: any, feed: {current: import('../hooks/useBlipFeed.js').BlipFeed}, onBack: Function}} props
 */
function FullMap({ engine, feed, onBack }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const view = useRef({ cx: 0, cz: 0, scale: 1, w: 1, h: 1 });
  const mark = useRef({ x: 0, z: 0, yaw: 0, live: false });

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;

    const draw = () => {
      const rect = wrap.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const map = engine?.level?.map;
      const base = getBase(map);
      const me = resolvePlayerMark(engine, feed.current, mark.current);

      if (!base) {
        drawPlaceholder(ctx, w, h, { spacing: 44, offsetX: w * 0.5, offsetY: h * 0.5 });
        ctx.font = '700 13px "Barlow Condensed", system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = MAP_COLORS.label;
        ctx.fillText('NO MAP DATA FOR THIS SCENE', w * 0.5, h * 0.5 - 34);
        drawPlayerMarker(ctx, w * 0.5, h * 0.5, me.yaw, 18);
        return;
      }

      const v = view.current;
      v.w = w;
      v.h = h;
      v.cx = (base.minX + base.maxX) * 0.5;
      v.cz = (base.minZ + base.maxZ) * 0.5;
      v.scale = fitScale(base, w, h, 64);

      ctx.fillStyle = MAP_COLORS.ocean;
      ctx.fillRect(0, 0, w, h);
      blitBase(ctx, base, v);
      drawLabels(ctx, map, v);
      // The south-west villages, UNDER the blips and the markers: they are
      // ground, not somebody standing on it. Nothing to draw on a level
      // without any — see `mapRender.js#drawVillages`.
      drawVillages(ctx, map, v);

      // The feed is frozen with the world, so `stamp > 0` ("it ever fired") is
      // the right test here — NOT the minimap's freshness test, which would
      // drop every blip a second after the menu opened.
      const f = feed.current;
      if (f.player.stamp > 0) {
        for (let i = 0; i < f.count; i++) {
          drawBlip(ctx, viewX(v, f.x[i]), viewY(v, f.z[i]), f.kind[i], 4.5);
        }
      }
      // Mission markers OVER the blips: the map's whole job while paused is
      // "where do I have to go", and a marker under a crowd of civilian dots is
      // a marker nobody finds. No clamping here — the full map shows the whole
      // island, so nothing can be off it.
      for (let i = 0; i < f.markerCount; i++) {
        const mk = f.markers[i];
        const px = viewX(v, mk.x);
        const py = viewY(v, mk.z);
        drawMissionMarker(ctx, px, py, mk, 8);
        drawMarkerLabel(ctx, px, py, mk, 12);
      }
      drawPlayerMarker(ctx, viewX(v, me.x), viewY(v, me.z), me.yaw, 18);
    };

    draw();
    // Canvas text does NOT honour `font-display: swap` — it takes whatever is
    // loaded at draw time and keeps it. The minimap self-corrects (it redraws
    // 20 times a second); this map draws once, so it waits for the HUD face.
    let stale = false;
    document.fonts?.ready?.then(() => {
      if (!stale) draw();
    });

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(draw) : null;
    ro?.observe(wrap);
    if (!ro) window.addEventListener('resize', draw);
    return () => {
      stale = true;
      ro?.disconnect();
      if (!ro) window.removeEventListener('resize', draw);
    };
  }, [engine, feed]);

  return (
    <div
      data-hud-overlay=""
      className="hud-fade-in pointer-events-auto absolute inset-0 z-50 bg-void"
    >
      <div ref={wrapRef} className="absolute inset-0">
        <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }} aria-hidden="true" />
      </div>

      <header className="pointer-events-none absolute left-6 top-5 select-none">
        <div className="gta-outline font-display text-2xl text-gold">HARBOURSIDE</div>
        <div className="font-hud text-[11px] uppercase tracking-[0.34em] text-hud-faint">City map</div>
      </header>

      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={onBack}
        className="absolute right-6 top-5 rounded-[3px] border border-hairline bg-ink/85 px-4 py-2 font-display text-sm uppercase tracking-wide text-hud transition hover:border-gold hover:text-gold"
      >
        Back
      </button>

      <div className="pointer-events-none absolute bottom-5 left-6 select-none rounded-md border border-hairline bg-void/85 px-3 py-2.5 backdrop-blur-sm">
        <div className="mb-1.5 font-hud text-[10px] font-bold uppercase tracking-[0.26em] text-hud-faint">
          Legend
        </div>
        <ul className="flex flex-col gap-1">
          <li className="flex items-center gap-2 font-hud text-[12px] leading-none text-hud-dim">
            <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
              <path d="M6 1 10 11 6 8.6 2 11z" fill="#ffffff" stroke="#0b0e14" strokeWidth="1" strokeLinejoin="round" />
            </svg>
            You
          </li>
          {/* Markers first, and drawn as RINGS — the legend has to make the
              same "somewhere to be" vs "somebody there" distinction the map
              does, or the two colours just look like more factions. */}
          {[
            { kind: 'giver', label: 'Mission' },
            { kind: 'giver_optional', label: 'Side job' },
            { kind: 'objective', label: 'Objective' },
          ].map((e) => (
            <li key={e.kind} className="flex items-center gap-2 font-hud text-[12px] leading-none text-hud-dim">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full border-2 bg-transparent"
                style={{ borderColor: MARKER_COLORS[e.kind] }}
              />
              {e.label}
            </li>
          ))}
          {BLIP_LEGEND.map((e) => (
            <li key={e.kind} className="flex items-center gap-2 font-hud text-[12px] leading-none text-hud-dim">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/70"
                style={{ backgroundColor: BLIP_COLORS[e.kind] }}
              />
              {e.label}
            </li>
          ))}
        </ul>
      </div>

      {/* BOTH keys, and they do DIFFERENT things — see the header's ladder note.
          Printing only one of them would leave whichever the player pressed
          feeling like the other one misfired. */}
      <p className="pointer-events-none absolute bottom-5 right-6 select-none font-hud text-[11px] uppercase tracking-[0.3em] text-hud-faint">
        M — close · Esc — menu
      </p>
    </div>
  );
}

/**
 * District names, biggest district first, dropping any that would collide with
 * one already placed. Cheap greedy pass — this runs on resize, not per frame.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {any} map
 * @param {import('./mapRender.js').MapView} v
 */
function drawLabels(ctx, map, v) {
  const labels = districtLabels(map);
  if (!labels.length) return;

  ctx.font = '700 11px "Barlow Condensed", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  /** @type {Array<{x: number, y: number}>} */
  const placed = [];
  for (const l of labels) {
    if (placed.length >= 16) break;
    const x = viewX(v, l.x);
    const y = viewY(v, l.z);
    if (x < 30 || y < 14 || x > v.w - 30 || y > v.h - 14) continue;
    let clash = false;
    for (const p of placed) {
      if (Math.abs(p.x - x) < 84 && Math.abs(p.y - y) < 20) {
        clash = true;
        break;
      }
    }
    if (clash) continue;
    placed.push({ x, y });

    const text = ZONE_LABELS[l.zone] ?? String(l.zone).toUpperCase();
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(5,7,11,0.9)';
    ctx.strokeText(text, x, y);
    ctx.fillStyle = MAP_COLORS.label;
    ctx.fillText(text, x, y);
  }
}

/**
 * Drops any key the sim thinks is held. Reached through `engine.systems` on
 * purpose: `createGameplay` already exposes its `Input` on the system object,
 * so the pause menu needs no new engine surface to do this.
 * @param {any} engine
 */
function clearGameInput(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return;
  for (const s of systems) {
    if (s?.name === 'gameplay') s.input?.clear?.();
  }
}
